import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { getActivePromoBanners } from '../services/supabaseService';
import { Link } from 'react-router-dom';

interface Promo {
  id: string | number;
  title?: string;
  description?: string;
  image_url?: string;
  link_url?: string;
  cta_text?: string;
  bg_color?: string;
}

export const PromoBanner: React.FC = () => {
  const [promos, setPromos] = useState<Promo[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const fetchPromos = async () => {
      try {
        const data = await getActivePromoBanners();
        if (Array.isArray(data) && data.length > 0) {
          setPromos(data);
        }
      } catch (err) {
        console.error('Failed to load promo banners:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPromos();
  }, []);

  const handleNext = () => {
    if (promos.length === 0) return;
    setCurrentIndex((prev) => (prev + 1) % promos.length);
  };

  const handlePrev = () => {
    if (promos.length === 0) return;
    setCurrentIndex((prev) => (prev - 1 + promos.length) % promos.length);
  };

  // 30 seconds rotation logic
  useEffect(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    if (isPlaying && promos.length > 1) {
      timerRef.current = setInterval(() => {
        handleNext();
      }, 30000); // 30 seconds as requested!
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isPlaying, promos.length, currentIndex]);

  if (loading || promos.length === 0) {
    return null;
  }

  const currentPromo = promos[currentIndex];

  // Safe attribute mapping
  const title = currentPromo.title || 'Novidade Cardumy';
  const description = currentPromo.description || 'Confira os anúncios e novidades de nossos parceiros!';
  const image = currentPromo.image_url || 'https://images.unsplash.com/photo-1607604276583-eef5d076aa5f?auto=format&fit=crop&q=80&w=1200';
  const link = currentPromo.link_url || '#';
  const cta = currentPromo.cta_text || 'Aproveitar';
  const bgGradient = currentPromo.bg_color || 'from-purple-900/40 via-slate-900/90 to-purple-950/40';

  const isExternal = link.startsWith('http://') || link.startsWith('https://');

  const bannerContent = (
    <div className={`relative w-full h-full flex flex-col md:flex-row items-center justify-between p-6 md:p-8 rounded-[24px] border border-white/5 bg-gradient-to-r ${bgGradient} overflow-hidden`}>
      {/* Label and Texts */}
      <div className="flex-1 z-10 text-left space-y-3 max-w-xl pr-4">
        <div className="flex items-center space-x-2">
          <span className="text-[9px] font-black tracking-widest uppercase bg-slate-800/80 text-purple-400 border border-purple-500/20 px-2.5 py-1 rounded-full flex items-center gap-1.5 shadow-sm">
            <i className="fas fa-bullhorn text-[8px] animate-pulse"></i>
            Espaço Publicitário
          </span>
          <span className="text-[9px] font-bold text-slate-500 uppercase tracking-widest hidden sm:inline">
            Apoiador {currentIndex + 1} de {promos.length}
          </span>
        </div>

        <div>
          <h2 className="text-lg md:text-xl font-black text-white leading-tight tracking-tight uppercase italic block">
            {title}
          </h2>
          <p className="text-xs text-slate-300 font-medium leading-relaxed mt-1.5 block">
            {description}
          </p>
        </div>

        <div className="pt-2 flex items-center space-x-3">
          {isExternal ? (
            <a 
              href={link} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="bg-purple-600 hover:bg-purple-500 text-white font-black text-[10px] uppercase tracking-widest py-2.5 px-5 rounded-xl shadow-lg shadow-purple-600/10 hover:shadow-purple-600/30 active:scale-95 transition-all inline-block"
            >
              {cta} <i className="fas fa-external-link-alt ml-1.5 text-[8px]"></i>
            </a>
          ) : (
            <Link 
              to={link}
              className="bg-purple-600 hover:bg-purple-500 text-white font-black text-[10px] uppercase tracking-widest py-2.5 px-5 rounded-xl shadow-lg shadow-purple-600/10 hover:shadow-purple-600/30 active:scale-95 transition-all inline-block"
            >
              {cta} <i className="fas fa-arrow-right ml-1.5 text-[8px]"></i>
            </Link>
          )}

          {/* Inline Controls */}
          {promos.length > 1 && (
            <div className="flex items-center space-x-1.5 bg-slate-950/40 border border-white/5 p-1 rounded-lg">
              <button 
                onClick={(e) => { e.preventDefault(); handlePrev(); }}
                className="w-6 h-6 rounded-md hover:bg-slate-800 flex items-center justify-center text-slate-400 hover:text-white transition-colors"
                title="Anterior"
              >
                <i className="fas fa-chevron-left text-[9px]"></i>
              </button>
              <button 
                onClick={(e) => { e.preventDefault(); setIsPlaying(!isPlaying); }}
                className="w-6 h-6 rounded-md hover:bg-slate-800 flex items-center justify-center text-slate-400 hover:text-white transition-colors"
                title={isPlaying ? "Pausar Rotação Autonôma" : "Iniciar Rotação Autonôma (30s)"}
              >
                <i className={`fas ${isPlaying ? 'fa-pause' : 'fa-play'} text-[9px]`}></i>
              </button>
              <button 
                onClick={(e) => { e.preventDefault(); handleNext(); }}
                className="w-6 h-6 rounded-md hover:bg-slate-800 flex items-center justify-center text-slate-400 hover:text-white transition-colors"
                title="Próximo"
              >
                <i className="fas fa-chevron-right text-[9px]"></i>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Image Space */}
      <div className="mt-4 md:mt-0 w-full md:w-56 h-32 md:h-28 rounded-2xl overflow-hidden border border-white/10 shadow-2xl relative shrink-0">
        <img 
          src={image} 
          alt={title}
          referrerPolicy="no-referrer"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" 
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/40 via-transparent to-transparent"></div>
      </div>
    </div>
  );

  return (
    <div className="w-full transition-all duration-300">
      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0, scale: 0.98, y: 5 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.98, y: -5 }}
          transition={{ duration: 0.3 }}
          className="relative w-full h-full"
        >
          {bannerContent}
        </motion.div>
      </AnimatePresence>

      {/* Indicator Dots */}
      {promos.length > 1 && (
        <div className="mt-2.5 flex justify-center items-center space-x-1.5">
          {promos.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`h-1.5 transition-all duration-300 rounded-full ${currentIndex === idx ? 'w-5 bg-purple-500' : 'w-1.5 bg-slate-800 hover:bg-slate-700'}`}
              title={`Ir para anúncio ${idx + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
